import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, onUnauthorized, tokenStore } from '@/lib/api';
import { setExportBrand, setExportLogo } from '@/lib/export/branding';
import type { AuthUser } from '@/types';

interface AuthContextValue {
  user: AuthUser | null;
  /** True until the initial session check resolves — routes wait on this. */
  initialising: boolean;
  signIn: (email: string, password: string, remember: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Re-read the signed-in user.
   *
   * The topbar and sidebar render from this object, so changing your own name
   * or picture has to push the new value back here — otherwise the avatar in
   * the corner stays stale until the next full page load.
   */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initialising, setInitialising] = useState(true);

  // Restore the session on boot when a token is present.
  useEffect(() => {
    let cancelled = false;

    if (!tokenStore.get()) {
      setInitialising(false);
      return () => {
        cancelled = true;
      };
    }

    api
      .get<AuthUser>('/auth/me')
      .then((value) => {
        if (!cancelled) setUser(value);
      })
      .catch(() => {
        tokenStore.clear();
      })
      .finally(() => {
        if (!cancelled) setInitialising(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Posters and PDF exports print the builder's logo.
  useEffect(() => setExportLogo(user?.company_logo_url), [user?.company_logo_url]);
  // The letterhead lines that print under it on every export.
  useEffect(
    () =>
      setExportBrand({
        legalName: user?.company_legal_name ?? user?.company_name ?? null,
        abn: user?.company_abn ?? null,
        address: user?.company_address ?? null,
      }),
    [user?.company_legal_name, user?.company_name, user?.company_abn, user?.company_address],
  );

  // A 401 anywhere in the app drops the session so guards can redirect.
  useEffect(() => onUnauthorized(() => setUser(null)), []);

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) return;
    try {
      setUser(await api.get<AuthUser>('/auth/me'));
    } catch {
      /* A failed refresh leaves the existing session alone. */
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string, remember: boolean) => {
    const result = await api.post<{ token: string; user: AuthUser }>(
      '/auth/login',
      { email, password, remember },
      { anonymous: true },
    );
    tokenStore.set(result.token);
    setUser(result.user);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Logging out locally matters more than the server acknowledging it.
    } finally {
      tokenStore.clear();
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, initialising, signIn, signOut, refresh }),
    [user, initialising, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
